import { InstancedMesh } from "@babylonjs/core/Meshes/instancedMesh";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { restoreChildSlot } from "./restoreChildSlot";

/**
 * Give an instance a mesh of its own. An InstancedMesh draws with its source's
 * material and ignores one assigned to it, so nothing can be put on one
 * placement alone. The instance is replaced by a real Mesh cloned from its
 * source (geometry stays shared), in the same place in the hierarchy and in
 * its parent's child order. A mesh that is not an instance is returned as it
 * is.
 *
 * The instance is disposed here. Whatever indexed it - the Renderer's slot
 * pins - is the caller's to update.
 */
export function promoteInstanceToMesh(mesh: AbstractMesh): AbstractMesh {
    if (!(mesh instanceof InstancedMesh)) return mesh;

    const source = mesh.sourceMesh;
    const parent = mesh.parent;
    const name = mesh.name;
    const order = parent?.getChildren() ?? [];

    // Clone source mesh (geometry is shared, not duplicated)
    const cloned = source.clone(name, parent);
    cloned.isPickable = mesh.isPickable;
    cloned.enablePointerMoveEvents = mesh.enablePointerMoveEvents;
    cloned.position.copyFrom(mesh.position);
    cloned.scaling.copyFrom(mesh.scaling);
    if (mesh.rotationQuaternion) {
        cloned.rotationQuaternion = mesh.rotationQuaternion.clone();
    } else {
        cloned.rotation.copyFrom(mesh.rotation);
    }

    // Dispose the instance — the clone takes its place in the hierarchy
    mesh.dispose();

    if (parent) {
        // Replacement keeps the old slot; the shared guard preserves unrelated observer reorders.
        restoreChildSlot(cloned, parent, order.map(child => child === mesh ? cloned : child));
    }
    return cloned;
}
